/**
 * Process Metrics Hook
 * FERP UNICORN 3: Process analytics and mining
 */

import { useState, useCallback, useEffect } from 'react';
import { useGraphQLClient } from '../../hooks/useGraphQLClient';
import { useCurrentTenant } from '../../hooks/useCurrentTenant';

const GET_PROCESS_ANALYTICS = `
  query GetProcessAnalytics($input: ProcessAnalyticsInput!) {
    getProcessAnalytics(input: $input) {
      executionMetrics {
        totalExecutions
        avgExecutionTime
        p50ExecutionTime
        p95ExecutionTime
        p99ExecutionTime
        successRate
        failureRate
      }
      bottlenecks {
        nodeId
        nodeName
        avgDuration
        percentOfTotal
        executionCount
        slowestExecution
        recommendation
      }
      slaCompliance {
        targetSLA
        actualAvg
        complianceRate
        violationCount
        violatingExecutions {
          executionId
          duration
          timestamp
        }
      }
      optimizationRecommendations {
        type
        confidence
        estimatedImprovement
        effort
        description
        implementation
      }
      heatmapData {
        hour
        dayOfWeek
        avgExecutionTime
        executionCount
      }
    }
  }
`;

export interface ExecutionMetrics {
  totalExecutions: number;
  avgExecutionTime: number;
  p50ExecutionTime: number;
  p95ExecutionTime: number;
  p99ExecutionTime: number;
  successRate: number;
  failureRate: number;
}

export interface Bottleneck {
  nodeId: string;
  nodeName: string;
  avgDuration: number;
  percentOfTotal: number;
  executionCount: number;
  slowestExecution: number;
  recommendation: string;
}

export interface SLACompliance {
  targetSLA: number;
  actualAvg: number;
  complianceRate: number;
  violationCount: number;
  violatingExecutions: Array<{
    executionId: string;
    duration: number;
    timestamp: string;
  }>;
}

export interface OptimizationRecommendation {
  type: 'PARALLELIZE' | 'CACHE' | 'ASYNC' | 'BATCH';
  confidence: number;
  estimatedImprovement: number;
  effort: number;
  description: string;
  implementation: string;
}

export interface HeatmapData {
  hour: number;
  dayOfWeek: number;
  avgExecutionTime: number;
  executionCount: number;
}

export interface ProcessAnalytics {
  executionMetrics: ExecutionMetrics;
  bottlenecks: Bottleneck[];
  slaCompliance: SLACompliance;
  optimizationRecommendations: OptimizationRecommendation[];
  heatmapData: HeatmapData[];
}

export function useProcessMetrics(workflowId: string, timeRange: { start: Date; end: Date }) {
  const client = useGraphQLClient();
  const { tenantId } = useCurrentTenant();
  const [analytics, setAnalytics] = useState<ProcessAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await client.query({
        query: GET_PROCESS_ANALYTICS,
        variables: {
          input: {
            workflowId,
            tenantId,
            startDate: timeRange.start.toISOString(),
            endDate: timeRange.end.toISOString()
          }
        }
      });

      if (result.errors?.length) {
        throw new Error(result.errors[0].message);
      }

      setAnalytics(result.data?.getProcessAnalytics);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch analytics'));
    } finally {
      setLoading(false);
    }
  }, [client, workflowId, tenantId, timeRange]);

  useEffect(() => {
    if (workflowId && tenantId) {
      fetchAnalytics();
    }
  }, [workflowId, tenantId, fetchAnalytics]);

  // Poll every 60 seconds
  useEffect(() => {
    const interval = setInterval(fetchAnalytics, 60000);
    return () => clearInterval(interval);
  }, [fetchAnalytics]);

  return {
    analytics,
    metrics: analytics?.executionMetrics || null,
    bottlenecks: analytics?.bottlenecks || [],
    slaCompliance: analytics?.slaCompliance || null,
    recommendations: analytics?.optimizationRecommendations || [],
    heatmapData: analytics?.heatmapData || [],
    loading,
    error,
    refetch: fetchAnalytics
  };
}
